// library imports
import {
  Button,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  Table,
} from "antd";
import { useState } from "react";
import FunctionalButton from "./FunctionalButton";

/* 
Note: if want to use list comprehension to be perform CRUD operation,
data must be in this format to use this function
(can refer to new E-Billing System)

The data passed into this function is a part of data from

  const data = {
    firstItem: [
      {
        key: 1,
        ColumnName1: "ColumnValue1",
        ColumnName2: "ColumnValue2",
      },
      {
        key: 2,
        ColumnName1: "ColumnValue1",
        ColumnName2: "ColumnValue2",
      },
    ],
    secondItem: [
      {
        key: 1,
        ColumnName3: "ColumnValue4",
        ColumnName3: "ColumnValue4",
      },
    ],
  });

E.g. data["firstItem"]
*/
export default function CustomEditTable({
  name = "",
  form,
  columnType,
  data,
  setData,
  handleDataType,
  addDataType,
  addFunction = false,
  addButtonText = "Add Row",
  deleteFunction = false,
  deleteDisabled = false,
  calculateFunction = false,
  getDimensionSelection = () => {},
  saveData = () => {},
  updateData = () => {},
  deleteData = () => {},
  calculateData = () => {},
  setEditClicked = () => {},
  handleUpload = () => {},
  uploadLoading = false,
  fileList = [],
  deleteFile = () => {},
  loading = false,
  addDisabled = false,
}) {
  const [dataState, setDataState] = useState(null);

  const [editingKey, setEditingKey] = useState("");
  const isEditing = (record) =>
    dataState
      ? dataState === "edit"
        ? record.ID === editingKey
        : record.key === editingKey
      : record.key === editingKey;

  const edit = (record) => {
    form.setFieldsValue({
      ...record,
    });
    setEditingKey(record.ID);
  };

  const handleCancel = (record) => {
    setEditingKey("");

    if (record.key) {
      const newData = data.filter((item) => item.key !== record["key"]);
      setData(newData);
    }
  };

  const handleDelete = (record) => {
    if (record.ID) {
      deleteData(record.ID);
    } else {
      const newData = data.filter((item) => item.key !== record["key"]);
      setData(newData);
    }
  };

  const handleAdd = () => {
    // reset the fields first so that previous values are not used
    form.resetFields();
    const newKey = data.length + 1;
    const newData = { key: newKey };
    setData([...data, newData]);
    setEditingKey(newKey);
  };

  const handleSave = async (record) => {
    try {
      setEditClicked(false);

      const row = await form.validateFields();

      if (dataState === "add") {
        setEditingKey("");
        saveData(row);
      } else if (dataState === "edit") {
        setEditingKey("");
        updateData(record.ID, row);
      }
      //   }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  // If emission source selected changes, update the dimension selection
  const handleValuesChange = (changedValues) => {
    if ("EmissionSource" in changedValues) {
      getDimensionSelection(changedValues.EmissionSource);
    }
  };

  const columns = [
    ...columnType,
    {
      title: "Action",
      dataIndex: "action",
      width: "180px",
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <Flex gap="middle" wrap>
            <Button
              onClick={() => handleSave(record)}
              style={{
                width: deleteFunction ? "64px" : "100px",
                border: "1px solid #303030",
              }}
              type="primary"
            >
              Save
            </Button>

            {deleteFunction ? (
              <FunctionalButton
                buttonClickFunction={() => handleDelete(record)}
                custom_button_style={{
                  width: "64px",
                  background: deleteDisabled ? "" : "red",
                }}
                disabled={deleteDisabled}
                children="Delete"
              />
            ) : (
              <></>
            )}

            <FunctionalButton
              buttonClickFunction={() => handleCancel(record)}
              custom_button_style={{
                width: deleteFunction ? "64px" : "100px",
                background: "white",
                color: "black",
              }}
              children="Cancel"
            />
          </Flex>
        ) : (
          <Flex gap="middle" wrap>
            {/* <Button
              onClick={() => {
                setEditClicked(true);
                setDataState("edit");
                edit(record);
              }}
              style={{
                width: "100px",
                border: "1px solid #303030",
              }}
              type="primary"
            >
              Edit
            </Button> */}

            {deleteFunction && (
              <FunctionalButton
                buttonClickFunction={() => handleDelete(record)}
                custom_button_style={{
                  width: "100px",
                  background: deleteDisabled ? "" : "red",
                }}
                disabled={deleteDisabled}
                children="Delete"
              />
            )}
          </Flex>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) return col;
    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: col.inputType ? col.inputType : "text",
        dataIndex: col.dataIndex,
        title: col.title,
        options: col.options,
        editing: isEditing(record),
        fieldOptions: col.fieldOptions ? col.fieldOptions : {},
        fieldComponent: col.fieldComponent ? col.fieldComponent : null,
        required: col.required ? col.required : false,
      }),
    };
  });

  return (
    <Form form={form} onValuesChange={handleValuesChange} autoComplete="off">
      <Flex justify="flex-start" gap={8} className="mb-2">
        {addFunction && (
          <Button
            // Disable add button if there is already one newly added row
            // Array.prototype.some() checks if at least one element in the array satisfies the condition.
            // Object.keys(item).length === 1: Ensures the object has exactly one property.
            // item.hasOwnProperty("key"): Ensures that the single property is key
            disabled={
              data.some(
                (item) =>
                  Object.keys(item).length === 1 && item.hasOwnProperty("key"),
              ) || addDisabled
            }
            onClick={() => {
              setDataState("add");
              handleAdd();
              getDimensionSelection(null); // when adding a new row, reset the dimension selection
            }}
            style={{
              width: "10%",
              background: addDisabled ? "" : "white",
              color: "black",
              border: "1px solid #303030",
            }}
            type="primary"
          >
            {addButtonText}
          </Button>
        )}
        {calculateFunction && (
          <Button
            onClick={calculateData}
            style={{
              width: "10%",
              border: "1px solid #303030",
            }}
            type="primary"
          >
            Calculate
          </Button>
        )}
      </Flex>
      <Table
        components={{ body: { cell: EditableCell } }}
        dataSource={data}
        size="small"
        columns={mergedColumns}
        scroll={{ y: 300 }}
        pagination={false}
        rowClassName="editable-row"
        loading={loading}
      />
    </Form>
  );
}

const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  options,
  fieldOptions,
  fieldComponent,
  children,
  required,
  ...restProps
}) => {
  let inputNode = <Input />;
  switch (inputType) {
    case "number":
      inputNode = <InputNumber style={{ width: "100%" }} {...fieldOptions} />;
      break;
    case "date":
      inputNode = <DatePicker {...fieldOptions} />;
      break;
    default:
      inputNode = <Input {...fieldOptions} />;
      break;
  }

  if (fieldComponent) {
    inputNode = fieldComponent;
  }

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{
            margin: 0,
          }}
          rules={[
            {
              required: required,
              message: `Please Input ${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};
